// 2. External libraries
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// 4. Type imports
import type { UseQueryResult } from '@tanstack/react-query';
import type { OAuthClientMetadata } from '~/api/oauth';

// 5. Custom hooks and utilities
import { getClientMetadataV1OauthClientsClientIdMetadataGet } from '~/generated/api/oauth/oauth';
import { queryKeys } from './queryKeys';

/**
 * Query hook for fetching OAuth client metadata.
 *
 * Fetches public metadata from GET /v1/oauth/clients/{clientId}/metadata endpoint.
 * Handles 404 responses by transforming them into { isRegistered: false } structure.
 *
 * Per §TS.5.1: Full generic type parameters for TanStack Query.
 *
 * @param clientId - OAuth client identifier. Query disabled when null.
 * @returns UseQueryResult containing client metadata or 404 fallback structure.
 *
 * @example
 * ```tsx
 * function OAuthAuthorizePage() {
 *   const clientId = searchParams.get('client_id');
 *   const { data: metadata, isLoading, error } = useOAuthClientMetadata(clientId);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (metadata?.isRegistered) {
 *     return <TrustedClientBadge name={metadata.clientName} />;
 *   }
 *   return <UnregisteredClientWarning clientId={clientId} />;
 * }
 * ```
 */
export function useOAuthClientMetadata(
  clientId: string | null
): UseQueryResult<OAuthClientMetadata, Error> {
  return useQuery<
    OAuthClientMetadata,
    Error,
    OAuthClientMetadata,
    ReturnType<typeof queryKeys.oauthClientMetadata>
  >({
    queryKey: queryKeys.oauthClientMetadata(clientId),
    queryFn: async () => {
      if (!clientId) {
        throw new Error('Client ID is required');
      }

      try {
        const data = await getClientMetadataV1OauthClientsClientIdMetadataGet(clientId);

        // Transform backend response (camelCase from Pydantic alias_generator)
        return {
          clientId: data.clientId,
          clientName: data.clientName ?? null,
          logoUri: data.logoUri ?? null,
          clientUri: data.clientUri ?? null,
          registeredAt: data.registeredAt ?? null,
          isRegistered: true,
        };
      } catch (err) {
        // Handle 404 by transforming into an unregistered-client structure.
        // Consumers branch on `isRegistered`; the remaining fields stay empty.
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          return {
            clientId,
            clientName: null,
            logoUri: null,
            clientUri: null,
            registeredAt: null,
            isRegistered: false,
          };
        }
        throw err;
      }
    },
    enabled: !!clientId,
    retry: (failureCount, error) => {
      // Don't retry on auth errors (401/403); retry other failures up to twice.
      return (
        failureCount < 2 &&
        !(axios.isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0))
      );
    },
  });
}
